import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { analyticsApi, getDefaultAccountId } from '../services/api';
import { AlertInfo } from '../types';

interface DashboardProps {
  showAlert: (message: string, severity: AlertInfo['severity']) => void;
}

interface DashboardStats {
  totalCampaigns: number;
  totalEmails: number;
  totalContacts: number;
  pendingFollowups: number;
  openRate: number;
  clickRate: number;
}

const Dashboard: React.FC<DashboardProps> = ({ showAlert }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalCampaigns: 0,
    totalEmails: 0,
    totalContacts: 0,
    pendingFollowups: 0,
    openRate: 0,
    clickRate: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const accountId = getDefaultAccountId();
      const overview = await analyticsApi.getOverview(accountId);

      setStats({
        totalCampaigns: overview.totalCampaigns || 0,
        totalEmails: overview.totalEmails || 0,
        totalContacts: overview.totalContacts || 0,
        pendingFollowups: overview.pendingFollowups || 0,
        openRate: overview.openRate || 0,
        clickRate: overview.clickRate || 0,
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showAlert('Failed to load dashboard data. Please check your connection.', 'error');
      // Keep stats at zero values instead of showing fake data
      setStats({
        totalCampaigns: 0,
        totalEmails: 0,
        totalContacts: 0,
        pendingFollowups: 0,
        openRate: 0,
        clickRate: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Campaigns',
      value: stats.totalCampaigns,
      icon: <EmailIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
      color: '#e3f2fd',
    },
    {
      title: 'Emails Sent',
      value: stats.totalEmails.toLocaleString(),
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#388e3c' }} />,
      color: '#e8f5e8',
    },
    {
      title: 'Total Contacts',
      value: stats.totalContacts.toLocaleString(),
      icon: <PeopleIcon sx={{ fontSize: 40, color: '#f57c00' }} />,
      color: '#fff3e0',
    },
    {
      title: 'Pending Follow-ups',
      value: stats.pendingFollowups,
      icon: <ScheduleIcon sx={{ fontSize: 40, color: '#7b1fa2' }} />,
      color: '#f3e5f5',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back! Here's an overview of your email outreach performance.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${card.color} 0%, white 100%)`,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.title}
                    </Typography>
                  </Box>
                  {card.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Performance Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Email Performance
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Open Rate</Typography>
                <Chip
                  label={`${stats.openRate}%`}
                  color={stats.openRate > 20 ? 'success' : 'warning'}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Click Rate</Typography>
                <Chip
                  label={`${stats.clickRate}%`}
                  color={stats.clickRate > 3 ? 'success' : 'warning'}
                  variant="outlined"
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Quick Actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quick action buttons and recent activity will be displayed here.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
